import React, { useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import { useNotification, AppNotification } from "../common/NotificationContext";
import { Badge, Menu, MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { readNotification } from "../services/dashboard.services";
import { getInsights } from "../services/dashboard.services";
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsDialog from "./SettingsDialog";
import { usePermissions } from "../context/PermissionsContext";


const Header = () => {
  const { notifications, markAsRead, removeNotification, clearNotifications } = useNotification();
  const { signOut, user } = usePermissions();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleSettingsOpen = () => setSettingsOpen(true);
  const handleSettingsClose = () => setSettingsOpen(false);
  const navigate = useNavigate();

  // Display name comes straight from the OIDC session (PermissionsContext
  // hydrates this from /api/auth/user-permissions). Fall back to email,
  // then a generic 'User' label only as a last resort.
  const displayName = user?.name || user?.email || 'User';

  const handleGotoDashboard = () => {
    navigate("/dashboard");
  };
  const handleLogout = async () => {
    // Clear local UI state, then delegate to PermissionsContext.signOut(),
    // which calls POST /api/auth/logout, clears the permissions cache, and
    // performs a full-page navigation to /login (so the SPA fully re-bootstraps
    // and cannot redirect back to /dashboard from stale auth state).
    // Cookies are NOT cleared here on purpose: the session and CSRF cookies are
    // HttpOnly/server-managed and are removed server-side by /api/auth/logout
    // (see cookie_session_store.clear_session_cookie). Deleting them from JS is
    // both impossible (HttpOnly) and unnecessary.
    localStorage.clear();
    clearNotifications();
    await signOut();
  };

  const handleNotificationClick = async (notif: AppNotification) => {
    if (!notif.clickable) {
    removeNotification(notif.id);
    return;
  }
    if (!notif.read) {
      const res = await readNotification(notif.id);
      if (res === "Success" || res?.status === "Success") {
        removeNotification(notif.id);
      } else {
        markAsRead(notif.id);
        removeNotification(notif.id);  
      }
    }
    const validationData = await getInsights(notif.id);
    navigate("/insights", {
      state: { validationData,docName:notif.docName,session:notif.session }
    });
    if (notif.status === "success") {
      try {
        const validationData = await getInsights(notif.id);
        navigate("/insights", { state: { validationData } });
      } catch (error: any) {
        console.error("Insights fetch error:", error.message);
        // Navigate with empty data if API fails
        navigate("/insights", { state: { validationData: { result: [] } } });
      }
    }
  };

  return (
    <header className="header full-width">
      <div className="flex header-logo-block">
        <div className="flex">
          <img
            src="/payassist-logo.svg"
            className="header-logo"
            style={{ cursor: 'pointer' }}
            onClick={handleGotoDashboard}
          />
        </div>
        <div className="header-sub-logo-block">
          <div>by &nbsp;</div>
          <img
            src="/deloitte-white-logo.png"
            alt="Logo"
            className="del-header-logo"
          />
        </div>
      </div>
      <div className="icons-div flex">
        {/* Settings Button */}
        <Tooltip title="Settings">
          <IconButton
            className="icons-btn"
            aria-label="Settings"
            style={{ marginRight: "8px" }}
            onClick={handleSettingsOpen}
          >
            <SettingsIcon sx={{ color: '#BFC1C2', fontSize: 22 }} />
          </IconButton>
        </Tooltip>

        <IconButton
          className="icons-btn"
          aria-label="Bell"
          style={{ marginRight: "16px" }}
          onClick={handleMenuOpen}
        >
          <Badge badgeContent={notifications.length} color="error">
            <img src="/icons/bell.svg" alt="Bell" />
          </Badge>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              bgcolor: "#26292E",
              color: "#BFC1C2",
              minWidth: 300,
              "& .MuiMenuItem-root": {
                bgcolor: "#26292E",
                color: "#BFC1C2",
                whiteSpace: "normal",
                wordBreak: "break-word",
                paddingY: 1,
                "&:hover": {
                  bgcolor: "#1C1E22",
                },
              },
            },
          }}
        >
          {notifications.length === 0 && <MenuItem>No notifications</MenuItem>}
          {notifications.map((notif:any) => (
            <MenuItem
              key={notif.id}
              onClick={() => handleNotificationClick(notif)}
              style={{ color: "#BFC1C2", backgroundColor: "#26292E" }}
            >
              {notif.message}
            </MenuItem>
          ))}
        </Menu>

        <div>
          <IconButton className="icons-btn" aria-label="User">
            <img src="/icons/user.svg" alt="User" />
          </IconButton>
          <label className="user-name">{displayName}</label>
        </div>

        <IconButton
          className="icons-btn"
          aria-label="Logout"
          onClick={handleLogout}
        >
          <img src="/icons/logout.svg" alt="Logout" />
        </IconButton>
      </div>

      {/* Settings Dialog */}
      <SettingsDialog open={settingsOpen} onClose={handleSettingsClose} />
    </header>
  );
};

export default Header;